import React from 'react';
import { VStack } from '@gluestack-ui/themed';
import { MinusIcon, PlusIcon, Pencil } from 'lucide-react-native';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DataTable } from './DataTable';
import useLinkedData from '../hooks/useLinkedData';
import { AddMemberModal } from './AddMemberModal';
import { useDialog } from '../../common/context/DialogContext';
import { EditMemberModal } from './EditMemberModal';
import GenericDataProvider from '../../common/api/GenericDataProvider';
import { AdminObjectName, AdminModelMap, ColumnDefinition } from './DataColumnConfig';

/**
 * T = The actual row type (defaults to AdminModelMap[K] for standard cases)
 * K = The API object name (e.g., 'users', 'modules')
 *
 * The second generic T allows components to use context-specific types (e.g., CourseStudent for courses/users)
 * while maintaining type safety in column definitions and callbacks.
 *
 * Note: T does not need to structurally extend AdminModelMap[K]. It just needs to represent
 * the actual data shape returned by the API in that specific context.
 */
interface MembershipControlProps<K extends AdminObjectName, T = AdminModelMap[K]> {
  objectName: K;
  parentObjectName: string;
  parentId: string;
  columns: Record<string, ColumnDefinition<T>>;
  icons?: Record<string, React.ElementType>;
  label?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addMemberFilter?: Record<string, any>;
  addModalColumns?: Record<string, ColumnDefinition<T>>;
  editModalColumns?: Record<string, ColumnDefinition<T>>;
  defaultSearchField?: string;
  searchableFields?: string[];
  defaultSort?: string;
  pageSize?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onLinkError?: (error: any) => void;
  noPadding?: boolean;
  noTitle?: boolean;
  showStateFilter?: boolean;
  excludeCurrent?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extraActions?: Record<string, any>;
  onSelectionChange?: (selected: T[]) => void;
  onLinkSuccess?: (id: string | number) => void;
  onUnlinkSuccess?: (id: string | number) => void;
  onEditMemberSuccess?: (variables: { id: string | number; data: Record<string, unknown> }) => Promise<void>;
  isActionRunning?: boolean;
  // Allows overriding the data source (e.g. from form state instead of API)
  overrideData?: T[];
  // Callbacks for controlling bulk selection state
  onClearSelection?: () => void;
  onExitSelectionMode?: () => void;
  // Confirmation overrides for unlinking
  unlinkConfirmationTitle?: string;
  unlinkConfirmationMessage?: string;
  doubleConfirmUnlink?: boolean;
  heightsOverrides?: {
    header?: number;
    tableHeader?: number;
    functionBar?: number;
    paddingVertical?: number;
    row?: number;
    pagination?: number;
    extraFooter?: number;
  };
}

/**
 * MembershipControl component for managing linked relationships in admin panels.
 *
 * @template K - The AdminObjectName (e.g., 'users', 'modules')
 * @template T - The actual row type (defaults to AdminModelMap[K])
 *
 * ## Architecture Pattern
 *
 * This component implements the "Container" pattern, orchestrating:
 * - Data fetching via useLinkedData
 * - Table display via DataTable
 * - Add/Edit/Delete modals
 * - Bulk operations with optimistic updates
 *
 * ## Type Safety Pattern
 *
 * Use dual generics when working with context-specific extended types:
 *
 * ```typescript
 * // For base types (default)
 * <MembershipControl<'modules'>
 *   columns={moduleColumns} // ColumnDefinition<Module>
 * />
 *
 * // For extended types (e.g., CourseStudent in course context)
 * <MembershipControl<'users', CourseStudent>
 *   columns={enrollmentColumns} // ColumnDefinition<CourseStudent>
 * />
 * ```
 *
 * ## Column Definition Pattern
 *
 * **IMPORTANT**: Extract columns to typed constants for proper type inference:
 *
 * ```typescript
 * const columns: Record<string, ColumnDefinition<CourseStudent>> = {
 *   lastname: {
 *     render: (row) => <Text>{row.lastname}</Text>, // row is CourseStudent
 *   },
 * };
 * ```
 *
 * Inline column definitions will NOT infer types correctly.
 *
 * @see .cursor/rules/admin-section-standards.mdc for full patterns
 */
export const MembershipControl = <K extends AdminObjectName, T = AdminModelMap[K]>({
  objectName,
  parentObjectName,
  parentId,
  columns,
  icons,
  label,
  addMemberFilter = {},
  addModalColumns,
  editModalColumns,
  defaultSearchField,
  searchableFields = [],
  defaultSort,
  pageSize,
  onLinkError,
  noPadding = false,
  noTitle = false,
  showStateFilter = false,
  excludeCurrent = false,
  extraActions = {},
  onSelectionChange,
  onLinkSuccess,
  onUnlinkSuccess,
  onEditMemberSuccess,
  isActionRunning: externalIsActionRunning,
  overrideData,
  onClearSelection,
  onExitSelectionMode,
  unlinkConfirmationTitle,
  unlinkConfirmationMessage,
  doubleConfirmUnlink = false,
  heightsOverrides,
}: MembershipControlProps<K, T>) => {
  const [addMemberModalVisible, setAddMemberModalVisible] = React.useState(false);
  const [editMemberModalVisible, setEditMemberModalVisible] = React.useState(false);
  const [editMemberData, setEditMemberData] = React.useState<Partial<T>>({});
  const [currentPage, setCurrentPage] = React.useState(1);
  const [searchText, setSearchText] = React.useState('');
  const [sort, setSort] = React.useState<string | null>(defaultSort || null);
  const [internalIsActionRunning, setInternalIsActionRunning] = React.useState(false);
  const [optimisticallyAddedIds, setOptimisticallyAddedIds] = React.useState<Set<string | number>>(new Set());
  const { showDialog } = useDialog();
  const { t } = useTranslation();

  const handleClearSelectionAction = React.useCallback(() => {
    // Clear selection without remounting the table to preserve pagination state
    onClearSelection?.();
    // Don't increment tableKey here - it causes pagination to reset
  }, [onClearSelection]);

  const handleExitSelectionModeAction = React.useCallback(() => {
    onExitSelectionMode?.();
  }, [onExitSelectionMode]);

  const isActionRunning = externalIsActionRunning || internalIsActionRunning;

  const {
    data, pagination, isLoading, linkMutation, unlinkMutation, updateLinkMutation,
  } = useLinkedData({
    parentObjectName,
    parentId,
    objectName,
    currentPage,
    searchText,
    defaultSearchField,
    searchableFields,
    sort,
    currentPageSize: pageSize,
  });

  // Fetch all current member IDs for exclusion in the add modal
  const { data: allMemberIds } = useQuery({
    queryKey: [parentObjectName, parentId, objectName, 'all-ids'],
    queryFn: async () => {
      // If we have override data, we already handled it in the memo above,
      // but we still return it here to keep the query result consistent if needed
      if (overrideData) {
        return overrideData.map((m) => (m as unknown as { id: string | number }).id);
      }
      const dataProvider = new GenericDataProvider();
      const result = await dataProvider.getLinkedItems(parentObjectName, parentId, objectName, {
        page: 1,
        pageSize: 10000,
        filter: {},
      });
      return result.data?.map((m: { id: string | number }) => m.id) || [];
    },
    enabled: !!(addMemberModalVisible && excludeCurrent && !overrideData),
    staleTime: 1000 * 60, // 1 minute stale time
  });

  // Derive all current member IDs for exclusion in the add modal
  const excludeIds = React.useMemo(() => {
    const ids = new Set<string | number>();

    // 1. If we have an override data source, use it immediately
    if (overrideData) {
      overrideData.forEach((m: T) => {
        const mid = (m as unknown as { id?: string | number }).id;
        if (mid) ids.add(mid);
      });
    }

    // 2. If we have fetched data from the API, add those too
    if (allMemberIds) {
      allMemberIds.forEach((id: string | number) => ids.add(id));
    }

    // 3. Always include optimistically added IDs
    optimisticallyAddedIds.forEach((id: string | number) => ids.add(id));

    return Array.from(ids).map(String);
  }, [overrideData, allMemberIds, optimisticallyAddedIds]);

  // Clear optimistic additions when the server data updates
  React.useEffect(() => {
    if (allMemberIds) {
      setOptimisticallyAddedIds(new Set());
    }
  }, [allMemberIds]);

  const memberActions = React.useMemo(() => ({
    ...(editModalColumns
      ? {
        edit: { label: 'Edit', icon: icons?.edit || Pencil, position: 'row' },
      }
      : {}),
    create: { label: 'Add', icon: icons?.add || PlusIcon, position: 'header' },
    delete: {
      label: 'Delete',
      icon: icons?.delete || MinusIcon,
      position: 'row',
    },
    ...extraActions,
  }), [editModalColumns, icons, extraActions]);

  const handleSearch = (text: string | null) => {
    setSearchText(text || '');
    setCurrentPage(1); // Reset to first page on new search
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSort = (field: string | null, direction: 'asc' | 'desc' | null) => {
    if (!field || !direction) {
      setSort(null);
    } else {
      setSort(direction === 'desc' ? `${field}-` : field);
    }
  };

  const executeUnlink = async (ids: (string | number)[]) => {
    setInternalIsActionRunning(true);
    try {
      await Promise.all(ids.map((id) => unlinkMutation.mutateAsync(id)));
      ids.forEach((id) => {
        if (onUnlinkSuccess) onUnlinkSuccess(id);
      });
    } catch (error) {
      if (onLinkError) onLinkError(error);
    } finally {
      setInternalIsActionRunning(false);
      handleClearSelectionAction();
      handleExitSelectionModeAction();
    }
  };

  const confirmUnlink = (ids: (string | number)[]) => {
    const isBulk = ids.length > 1;
    const title = unlinkConfirmationTitle || (isBulk ? t('common.buttons.delete', { defaultValue: 'Delete' }) : t('common.buttons.delete', { defaultValue: 'Delete' }));
    const message = unlinkConfirmationMessage || (isBulk ? t('admin.common.confirm_bulk_delete', { count: ids.length, defaultValue: `Are you sure you want to delete ${ids.length} items?` }) : t('admin.common.confirm_delete', { defaultValue: 'Are you sure you want to delete this item?' }));

    const showSecondConfirmation = () => {
      showDialog({
        title: t('errors.membership.confirm_removal_title', { defaultValue: 'Final Confirmation Required' }),
        message: t('errors.membership.confirm_removal_message', { defaultValue: 'Are you absolutely sure? All data related to this context will be permanently deleted.' }),
        buttons: [
          {
            text: t('common.buttons.cancel', { defaultValue: 'Cancel' }),
            variant: 'outline',
            action: 'secondary',
          },
          {
            text: t('common.buttons.delete', { defaultValue: 'Remove' }),
            action: 'negative',
            onPress: () => {
              executeUnlink(ids);
            },
          },
        ],
      });
    };

    showDialog({
      title,
      message,
      buttons: [
        {
          text: t('common.buttons.cancel', { defaultValue: 'Cancel' }),
          variant: 'outline',
          action: 'secondary',
        },
        {
          text: t('common.buttons.delete', { defaultValue: 'Remove' }),
          action: 'negative',
          preventAutoClose: doubleConfirmUnlink,
          onPress: (closeDialog) => {
            if (doubleConfirmUnlink) {
              showSecondConfirmation();
            } else {
              executeUnlink(ids);
              closeDialog?.();
            }
          },
        },
      ],
    });
  };

  const handleMemberAction = async (action: string, actionData: T | T[]) => {
    if (action === 'create') {
      setAddMemberModalVisible(true);
      return;
    }
    if (action === 'delete') {
      confirmUnlink([(actionData as unknown as { id: string | number }).id]);
      return;
    }
    if (action === 'bulkDelete') {
      const itemsToDelete = Array.isArray(actionData) ? actionData : [actionData];
      if (itemsToDelete.length > 0) {
        confirmUnlink(itemsToDelete.map((item: T) => (item as unknown as { id: string | number }).id));
      }
      return;
    }
    if (action === 'edit') {
      setEditMemberData(actionData as T);
      setEditMemberModalVisible(true);
      return;
    }

    // Handle extra actions
    if (extraActions[action]?.onAction) {
      setInternalIsActionRunning(true);
      try {
        await extraActions[action].onAction(actionData);
        // After successful bulk operation, clear selection and exit selection mode
        if (Array.isArray(actionData) && actionData.length > 0) {
          handleClearSelectionAction();
          handleExitSelectionModeAction();
        }
      } finally {
        setInternalIsActionRunning(false);
      }
    }
  };

  const handleAddMember = async (id: string | number) => {
    // Optimistically add to exclusion list immediately
    setOptimisticallyAddedIds((prev) => new Set([...prev, id]));
    setInternalIsActionRunning(true);
    try {
      await linkMutation.mutateAsync(id);
      if (onLinkSuccess) onLinkSuccess(id);
    } catch (error) {
      // Remove from optimistic set on failure
      setOptimisticallyAddedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      if (onLinkError) onLinkError(error);
    } finally {
      setInternalIsActionRunning(false);
    }
  };

  return (
    <VStack space="md" bg="$backgroundLight0" py="$4" px={noPadding ? 0 : '$6'}>
      <DataTable
        dataConfig={{
          objectName,
          parentObjectName,
          parentId,
          // Cast to AdminModelMap[K][] for DataTable compatibility
          // DataTable internally works with base types, but our T may be extended
          data: (overrideData || data) as unknown as AdminModelMap[K][],
          pagination,
          disableDataFetching: true,
          isLoading,
        }}
        displayConfig={{
          tableTitle: label,
          noPadding,
          noTitle,
          heightsOverrides,
          // Cast columns to base type for DataTable
          columns: columns as unknown as Record<string, ColumnDefinition<AdminModelMap[K]>>,
        }}
        actionConfig={{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          actions: memberActions as any, // Typed as Record<string, Partial<ActionConfig>> internally
          onAction: handleMemberAction,
          isActionRunning,
        }}
        searchFilterConfig={{
          showStateFilter,
          defaultSearchField,
          searchableFields,
        }}
        callbacks={{
          onSearch: handleSearch,
          onPageChange: handlePageChange,
          onSort: handleSort,
          // Cast selection callback to base type
          onSelectionChange: onSelectionChange as ((selected: AdminModelMap[K][]) => void) | undefined,
          onClearSelection: handleClearSelectionAction,
          onExitSelectionMode: handleExitSelectionModeAction,
        }}
        paginationSelectionConfig={{
          pageSize,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          enableSelection: !!Object.values(memberActions).some((a: any) => (a as { bulk?: boolean }).bulk),
        }}
      />

      <AddMemberModal
        visible={addMemberModalVisible}
        hideModal={() => setAddMemberModalVisible(false)}
        objectName={objectName}
        columns={addModalColumns || columns}
        icons={icons}
        title={`Add ${objectName}`}
        onAddMember={handleAddMember}
        onError={onLinkError}
        filter={addMemberFilter}
        defaultSearchField={defaultSearchField}
        searchableFields={searchableFields}
        excludeIds={excludeIds}
      />

      {editModalColumns && (
        <EditMemberModal
          key={`edit-modal-${(editMemberData as { id?: string | number })?.id || 'new'}`}
          visible={editMemberModalVisible}
          hideModal={() => setEditMemberModalVisible(false)}
          title={`Edit ${objectName} in ${parentObjectName}`}
          columns={editModalColumns || columns}
          memberData={editMemberData}
          parentId={parentId}
          /**
           * Pattern: Modal commits changes only on explicit Save.
           * Parent components can provide onEditMemberSuccess to handle additional
           * state updates (e.g., segment reordering in CourseModulesForm).
           */
          onEditMember={async (variables) => {
            setInternalIsActionRunning(true);
            try {
              if (onEditMemberSuccess) {
                // Use parent's custom handler if provided (e.g., for segment updates)
                await onEditMemberSuccess({
                  id: variables.id,
                  data: variables.data,
                });
              } else {
                // Default behavior: just update the member via API
                await updateLinkMutation.mutateAsync({
                  id: variables.id,
                  data: variables.data,
                });
              }
            } catch (error) {
              if (onLinkError) onLinkError(error);
            } finally {
              setInternalIsActionRunning(false);
            }
          }}
        />
      )}
    </VStack>
  );
};
